import React, { useState, useEffect } from 'react';
import { FileText, Download, Loader2, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentationPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/docs/list`);
      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (err) {
      setError('Failed to load documents');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const categorizeDocuments = (docs) => {
    const categories = {
      'Sales & Marketing': [],
      'Technical': [],
      'Security & Compliance': [],
      'Other': []
    };

    docs.forEach(doc => {
      const name = doc.filename.toLowerCase();
      if (name.includes('sales') || name.includes('brochure') || name.includes('client')) {
        categories['Sales & Marketing'].push(doc);
      } else if (name.includes('deployment') || name.includes('architecture') || name.includes('technical') || name.includes('database')) {
        categories['Technical'].push(doc);
      } else if (name.includes('security') || name.includes('rbac') || name.includes('compliance')) {
        categories['Security & Compliance'].push(doc);
      } else {
        categories['Other'].push(doc);
      }
    });

    return categories;
  };

  const handleDownload = (url, filename) => {
    const fullUrl = `${API_URL}${url}`;
    window.open(fullUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-red-400">{error}</div>
      </div>
    );
  }

  const categories = categorizeDocuments(documents);

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            MyHR Orbit Documentation
          </h1>
          <p className="text-gray-400 text-lg">
            Download sales materials, technical guides, and deployment documentation
          </p>
        </div>

        {/* Document Categories */}
        {Object.entries(categories).map(([category, docs]) => {
          if (docs.length === 0) return null;
          
          return (
            <div key={category} className="mb-10">
              <h2 className="text-2xl font-semibold text-white mb-6 border-b border-gray-700 pb-2">
                {category}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {docs.map((doc) => (
                  <div 
                    key={doc.filename}
                    className="bg-gray-800 rounded-lg p-5 border border-gray-700 hover:border-blue-500 transition-all"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <FileText className="w-6 h-6 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate" title={doc.title}>
                          {doc.title}
                        </h3>
                        <p className="text-gray-500 text-sm truncate">
                          {doc.filename}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {/* PDF Download Button */}
                      <button
                        onClick={() => handleDownload(doc.pdf_url, doc.filename.replace('.md', '.pdf'))}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
                        data-testid={`download-pdf-${doc.filename}`}
                      >
                        <Download className="w-4 h-4" />
                        PDF
                      </button>
                      
                      {/* Markdown Download Button */}
                      <button
                        onClick={() => handleDownload(doc.download_url, doc.filename)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md text-sm font-medium transition-colors"
                        data-testid={`download-md-${doc.filename}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                        Markdown
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Footer */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>MyHR Orbit Documentation System</p>
          <p>AurousOrbit Sdn Bhd © 2026</p>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;
